/**
 * useContractSoftWarnings
 *
 * Computes anticipatory "soft warnings" per domain (CAMPO, QUALIDADE, FINANCEIRO)
 * without blocking any flow. Thresholds come from emp_config.configuracoes JSONB —
 * never hardcoded. Fallback defaults apply when config is absent.
 *
 * Rules:
 *  CAMPO      — executions that have no evidence attached
 *  QUALIDADE  — executions approved (status='aprovado') with qualidade_status IS NULL
 *               and created more than qg_alert_days ago
 *  FINANCEIRO — executions approved with no boletim covering them,
 *               created more than medicao_alert_days ago
 *
 * Design decisions:
 *  • Each domain reads only its own tables → zero cross-domain coupling
 *  • "Last valid QG status" = qualidade_status column (it is the current state; no
 *    separate history table exists)
 *  • emp_config is keyed by cluster_id; caller must provide both ids
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DomainWarning {
  /** Human-readable label for the badge tooltip */
  label: string;
  /** Count of affected items (used for badge number) */
  count: number;
}

export interface ContractSoftWarnings {
  campo: DomainWarning | null;
  qualidade: DomainWarning | null;
  financeiro: DomainWarning | null;
  isLoading: boolean;
}

/** Reads thresholds from emp_config.configuracoes.thresholds, with safe fallbacks */
interface Thresholds {
  qg_alert_days: number;
  medicao_alert_days: number;
}

async function fetchThresholds(clusterId: string | undefined): Promise<Thresholds> {
  const defaults: Thresholds = { qg_alert_days: 5, medicao_alert_days: 10 };
  if (!clusterId) return defaults;

  const { data } = await supabase
    .from('emp_config')
    .select('configuracoes')
    .eq('cluster_id', clusterId)
    .maybeSingle();

  if (!data?.configuracoes) return defaults;

  const cfg = data.configuracoes as Record<string, unknown>;
  const thresholds = (cfg.thresholds ?? {}) as Record<string, unknown>;

  return {
    // qg_alert_days: explicit key OR fall back to prazo_validacao_execucao_dias
    qg_alert_days:
      typeof thresholds.qg_alert_days === 'number'
        ? thresholds.qg_alert_days
        : typeof thresholds.prazo_validacao_execucao_dias === 'number'
        ? thresholds.prazo_validacao_execucao_dias
        : defaults.qg_alert_days,

    // medicao_alert_days: explicit key OR default
    medicao_alert_days:
      typeof thresholds.medicao_alert_days === 'number'
        ? thresholds.medicao_alert_days
        : defaults.medicao_alert_days,
  };
}

export function useContractSoftWarnings(
  contratoId: string,
  clusterId?: string,
): ContractSoftWarnings {
  const { data, isLoading } = useQuery({
    queryKey: ['contract-soft-warnings', contratoId, clusterId],
    queryFn: async () => {
      const thresholds = await fetchThresholds(clusterId);

      const cutoffQG = new Date();
      cutoffQG.setDate(cutoffQG.getDate() - thresholds.qg_alert_days);

      const cutoffMedicao = new Date();
      cutoffMedicao.setDate(cutoffMedicao.getDate() - thresholds.medicao_alert_days);

      // ── Fetch all approved executions for this contract in one call ──────
      const { data: execucoes } = await supabase
        .from('emp_validacoes_execucao')
        .select('id, status, qualidade_status, created_at')
        .eq('contrato_id', contratoId)
        .in('status', ['disponivel_para_medicao', 'vinculada_a_boletim']);

      const approved = execucoes ?? [];
      const approvedIds = approved.map(e => e.id);

      // ── CAMPO: executions without any evidence ────────────────────────────
      let campoCount = 0;
      if (approvedIds.length > 0) {
        // Fetch evidencias for approved executions; find which have none
        const { data: evidencias } = await supabase
          .from('emp_evidencias')
          .select('execucao_id')
          .in('execucao_id', approvedIds);

        const idsComEvidencia = new Set((evidencias ?? []).map(e => e.execucao_id));
        campoCount = approvedIds.filter(id => !idsComEvidencia.has(id)).length;
      }

      // ── QUALIDADE: approved executions older than qg_alert_days with null QG ──
      const qualidadeCount = approved.filter(e => {
        if (e.qualidade_status !== null) return false; // QG already initiated
        const created = new Date(e.created_at);
        return created < cutoffQG;
      }).length;

      // ── FINANCEIRO: approved executions older than medicao_alert_days
      //               not covered by any boletim ─────────────────────────────
      let financeiroCount = 0;
      const financeiroCandidates = approved.filter(e => {
        const created = new Date(e.created_at);
        return created < cutoffMedicao;
      });

      if (financeiroCandidates.length > 0) {
        // A boletim "covers" an execution when its periodo_fim >= execucao.data_execucao
        // (we approximate via checking if any boletim exists for this contract)
        // More precise: check if execution's date falls within any approved/enviado boletim period
        const { data: boletins } = await supabase
          .from('emp_boletins_medicao')
          .select('periodo_inicio, periodo_fim, status')
          .eq('contrato_id', contratoId)
          .in('status', ['enviado', 'aprovado']);

        // Refetch the dates for those executions
        const { data: execDates } = await supabase
          .from('emp_validacoes_execucao')
          .select('id, data_execucao')
          .in('id', financeiroCandidates.map(e => e.id));

        const bols = boletins ?? [];
        financeiroCount = (execDates ?? []).filter(e => {
          const execDate = e.data_execucao;
          const covered = bols.some(b => execDate >= b.periodo_inicio && execDate <= b.periodo_fim);
          return !covered;
        }).length;
      }

      return { campoCount, qualidadeCount, financeiroCount, thresholds };
    },
    enabled: !!contratoId,
    staleTime: 60_000, // 1 min — these are soft signals, not critical paths
    refetchInterval: 5 * 60_000, // refresh every 5 min
  });

  if (!data || isLoading) {
    return { campo: null, qualidade: null, financeiro: null, isLoading };
  }

  const { campoCount, qualidadeCount, financeiroCount, thresholds } = data;

  return {
    campo: campoCount > 0
      ? { label: `${campoCount} execução(ões) sem evidência`, count: campoCount }
      : null,
    qualidade: qualidadeCount > 0
      ? { label: `${qualidadeCount} execução(ões) sem QG há >${thresholds.qg_alert_days}d`, count: qualidadeCount }
      : null,
    financeiro: financeiroCount > 0
      ? { label: `${financeiroCount} execução(ões) sem medição há >${thresholds.medicao_alert_days}d`, count: financeiroCount }
      : null,
    isLoading: false,
  };
}
